import React, { useEffect } from "react"
import type { TextProps } from "@chakra-ui/react"
import { Box, Flex, Text } from "@chakra-ui/react"
import { cleanTitle } from "components/Contentful/RichTextRenderer/RichTextRenderer.utils"
import type { FeaturedProducts } from "types/cms"
import { ProductCardGrid } from "components/ProductCard/ProductCardGrid"
import { useLandingPageContext } from "packages/acquisition/components/chakra-components/LandingPage/LandingPageProvider"
import { useLocale } from "hooks"
import { Link } from "components/Link"
import type { CMSPageProps } from "packages/acquisition/components/chakra-components/LandingPage/LandingPage.utils"
import type { VariantType } from "types"
import { getColour } from "utils/getColour"
import { fonts } from "theme/themeConfig"
import { Headline, InnerContainer, OuterContainer } from "../styles"
import { footerCopy, legalCopy } from "./FeatureProductsQuickShop.constants"
import { useIsMember } from "./FeatureProductsQuickShop.hooks"

type Props = CMSPageProps & FeaturedProducts

export const FeaturedProductsQuickShop: React.FC<Props> = ({
  trackingModule,
  variants,
  title,
  gridTemplateColumns,
  backgroundColour,
}) => {
  const isMember = useIsMember()
  const locale = useLocale()
  const { ctVariants, quickShopTracking, setQuickShopTracking } =
    useLandingPageContext()
  const { modulePosition } = trackingModule
  const headerTextAlign: TextProps["align"] = {
    lg: "center",
    base: "left",
  }

  const headerWidth: TextProps["width"] = {
    base: "max-content",
    lg: "full",
  }

  const items = variants
    .map((variant) =>
      ctVariants?.find((ctVariant) => ctVariant.sku === variant.sku)
    )
    .filter(Boolean)

  useEffect(() => {
    if (quickShopTracking?.trackingModule?.pageId !== trackingModule.pageId) {
      setQuickShopTracking({ trackingModule })
    }
  }, [setQuickShopTracking, trackingModule, quickShopTracking])

  const gridColumnsOnLargeDisplays =
    items && items.length < 4 ? "repeat(3, 1fr)" : "repeat(4, 1fr)"

  const defaultGridTemplateColumns = [
    "repeat(2, 1fr)",
    "repeat(3, 1fr)",
    gridColumnsOnLargeDisplays,
  ]

  return (
    <OuterContainer backgroundColor={getColour(backgroundColour)}>
      <InnerContainer gap={isMember ? "2.5rem" : 0}>
        <Flex
          justify="space-between"
          alignItems="center"
          flexDirection={!isMember ? { base: "column", md: "row" } : "column"}
          w="full"
          px={{
            base: "1.5rem",
            md: "5rem",
            lg: "7.5rem",
          }}
          py={!isMember ? { base: "1.5rem", md: "3.75rem" } : {}}
        >
          <Headline
            variant="apercu"
            as={modulePosition === 1 ? "h1" : "h2"}
            textAlign={isMember ? headerTextAlign : {}}
            width={isMember ? headerWidth : {}}
            id={cleanTitle(title)}
          >
            {title}
          </Headline>
          {!isMember && (
            <Text
              maxWidth={{ base: "unset", md: "36.25rem" }}
              variant="acumin"
              maxW="900px"
              data-testid="quickshop-disclaimer"
            >
              {legalCopy[locale]}
            </Text>
          )}
        </Flex>
        {ctVariants && (
          <Box
            px={{
              base: "1.5rem",
              md: "5rem",
              lg: "7.5rem",
            }}
          >
            <ProductCardGrid
              variants={items as VariantType[]}
              showQuickShopButton
              gridTemplateColumns={
                gridTemplateColumns || defaultGridTemplateColumns
              }
            />
          </Box>
        )}
      </InnerContainer>
      {!isMember && (
        <Flex w="full" alignItems="center" justifyContent="center" mt="2.5rem">
          <Text variant="acumin" maxW="900px" fontSize={-4}>
            {footerCopy}
            <Link
              fontFamily={fonts.acuminLight}
              textDecoration="underline"
              href="/legal/terms-and-conditions"
            >
              Terms apply
            </Link>
          </Text>
        </Flex>
      )}
    </OuterContainer>
  )
}
